import { useCallback, useEffect, useState } from 'react';
import { displayName, type UserProfile } from '@visionqc/shared';
import { api } from '../api/client';
import { useAuth } from '../api/auth';
import { fmtTime } from '../lib/format';

/**
 * One user, in full.
 *
 * Same shape as the station page on purpose: identity first, then what they
 * have actually been doing. An admin opening this is usually asking one of two
 * things — "can this person still get in" or "what did they label".
 */

export interface UserDetail extends UserProfile {
  stats: {
    annotations: number;
    good: number;
    bad: number;
    unsure: number;
    firstAnnotation: string | null;
    lastAnnotation: string | null;
    activeSessions: number;
    lastSeenAt: string | null;
    hardwareActions: number;
    pageViews: number;
  };
  stations: Array<{ id: number; name: string; active: boolean; lastSeenAt: string | null }>;
  recentAnnotations: Array<{
    createdAt: string;
    label: string;
    imageKey: string;
    product: string | null;
  }>;
}

const LABEL_CLASS: Record<string, string> = { GOOD: 'good', BAD: 'bad', UNSURE: 'unsure' };

function Row({ label, value, link }: { label: string; value?: string | null; link?: string | null }) {
  return (
    <tr>
      <td className="muted" style={{ width: 150, borderBottom: 'none', padding: '5px 0' }}>
        {label}
      </td>
      <td style={{ borderBottom: 'none', padding: '5px 0' }}>
        {value ? link ? <a href={link}>{value}</a> : value : <span className="muted">—</span>}
      </td>
    </tr>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ minWidth: 110 }}>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {hint && (
        <div className="muted" style={{ fontSize: 11 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export function UserDetailView({
  id,
  onBack,
  onEdit,
}: {
  id: number;
  onBack: () => void;
  onEdit: (u: UserProfile) => void;
}) {
  const me = useAuth().user;
  const [d, setD] = useState<UserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setD(await api.userDetail(id));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <div className="panel" style={{ color: 'var(--bad)' }}>{error}</div>;
  if (!d) return <div className="spinner">Loading user…</div>;

  const s = d.stats;
  const locked = d.lockedUntil && new Date(d.lockedUntil) > new Date();

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <button className="btn sm" onClick={onBack}>
            ← All users
          </button>
          <h1 className="view-title" style={{ marginTop: 8 }}>
            {displayName(d)}
            <span className="muted" style={{ fontWeight: 400, fontSize: 15 }}>
              {' '}
              · {d.role}
              {!d.active && ' · deactivated'}
              {d.id === me?.id && ' · you'}
            </span>
          </h1>
        </div>
        <div className="row">
          {locked && (
            <button
              className="btn sm"
              onClick={async () => {
                await api.unlockUser(d.id);
                setNotice('Lockout cleared.');
                await load();
              }}
            >
              Unlock
            </button>
          )}
          <button className="btn sm" onClick={() => onEdit(d)}>
            ✎ Edit
          </button>
        </div>
      </div>

      {notice && <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>}

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <div className="panel">
          <strong>Person</strong>
          <table style={{ marginTop: 8 }}>
            <tbody>
              <Row label="First name" value={d.firstName} />
              <Row label="Last name" value={d.lastName} />
              <Row label="Email" value={d.email} link={d.email ? `mailto:${d.email}` : null} />
              <Row label="Mobile" value={d.phoneMobile} link={d.phoneMobile ? `tel:${d.phoneMobile}` : null} />
              <Row
                label="Address"
                value={
                  [
                    [d.street, d.houseNumber].filter(Boolean).join(' '),
                    [d.postalCode, d.city].filter(Boolean).join(' '),
                    d.country,
                  ]
                    .filter(Boolean)
                    .join(', ') || null
                }
              />
              {d.notes && <Row label="Notes" value={d.notes} />}
            </tbody>
          </table>
        </div>

        <div className="panel">
          <strong>Company</strong>
          <table style={{ marginTop: 8 }}>
            <tbody>
              <Row label="Name" value={d.companyName} />
              <Row
                label="Address"
                value={
                  [
                    [d.companyStreet, d.companyHouseNumber].filter(Boolean).join(' '),
                    [d.companyPostalCode, d.companyCity].filter(Boolean).join(' '),
                    d.companyCountry,
                  ]
                    .filter(Boolean)
                    .join(', ') || null
                }
              />
              <Row
                label="Phone"
                value={d.companyPhone}
                link={d.companyPhone ? `tel:${d.companyPhone}` : null}
              />
            </tbody>
          </table>

          <strong style={{ display: 'block', marginTop: 18 }}>Access</strong>
          <table style={{ marginTop: 8 }}>
            <tbody>
              <Row label="Login name" value={d.name} />
              <Row label="Role" value={d.role} />
              {/* What credentials exist, never anything derived from them. */}
              <Row
                label="Credentials"
                value={
                  [d.hasPassword ? 'password' : null, d.hasPin ? 'kiosk PIN' : null]
                    .filter(Boolean)
                    .join(' + ') || 'none — cannot sign in'
                }
              />
              <Row
                label="Locked"
                value={locked ? `until ${fmtTime(d.lockedUntil!)}` : 'no'}
              />
              <Row label="Active sessions" value={String(s.activeSessions)} />
              <Row label="Last seen" value={s.lastSeenAt ? fmtTime(s.lastSeenAt) : 'never signed in'} />
              <Row label="Account created" value={fmtTime(d.createdAt)} />
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 16 }}>
        <strong>Work</strong>
        <div className="row" style={{ gap: 28, flexWrap: 'wrap', marginTop: 12 }}>
          <Stat
            label="Annotations"
            value={s.annotations.toLocaleString()}
            hint={s.lastAnnotation ? `last ${fmtTime(s.lastAnnotation)}` : 'none yet'}
          />
          <Stat label="Good" value={s.good.toLocaleString()} />
          <Stat label="Bad" value={s.bad.toLocaleString()} />
          <Stat label="Unsure" value={s.unsure.toLocaleString()} hint="left for review" />
          <Stat label="Hardware actions" value={s.hardwareActions.toLocaleString()} />
          <Stat label="Page views" value={s.pageViews.toLocaleString()} />
        </div>
        {/* Attribution is by name so history survives a deletion — which means
            a rename leaves the old work behind. Say so rather than presenting a
            number that may quietly undercount. */}
        <p className="muted" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
          Counted by the login name <code>{d.name}</code>. Annotations record the name rather than
          the account, so history survives a deletion — but work done under a previous name is not
          included here.
        </p>
      </div>

      {d.stations.length > 0 && (
        <div className="panel" style={{ marginTop: 16 }}>
          <strong>Responsible for</strong>
          <table style={{ marginTop: 8 }}>
            <tbody>
              {d.stations.map((st) => (
                <tr key={st.id}>
                  <td>
                    {st.name}
                    {!st.active && <span className="muted"> · disabled</span>}
                  </td>
                  <td className="muted" style={{ textAlign: 'right' }}>
                    {st.lastSeenAt ? `last seen ${fmtTime(st.lastSeenAt)}` : 'never seen'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {d.recentAnnotations.length > 0 && (
        <div className="panel" style={{ marginTop: 16 }}>
          <strong>Most recent annotations</strong>
          <table style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>When</th>
                <th>Product</th>
                <th>Label</th>
              </tr>
            </thead>
            <tbody>
              {d.recentAnnotations.map((a, i) => (
                <tr key={i}>
                  <td className="muted">{fmtTime(a.createdAt)}</td>
                  <td>{a.product ?? <span className="muted">—</span>}</td>
                  <td>
                    <span className={`badge ${LABEL_CLASS[a.label] ?? ''}`}>{a.label}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
